import { ROUTES } from '@shared/config/routes';

export const landingNavItems = [
  { id: 'vision', label: 'Our Vision' },
  { id: 'products', label: 'Products' },
] as const;

export type LandingSectionId = (typeof landingNavItems)[number]['id'];

export interface LandingHeroContent {
  titleLines: readonly [string, string];
  description: string;
}

export const landingHeroTitleFirstLine: LandingHeroContent['titleLines'][0] =
  'Admission Intelligence.';

export const landingHeroTitleSecondLine: LandingHeroContent['titleLines'][1] = 'Redefined.';

export const landingHeroDescription: LandingHeroContent['description'] =
  'A full admissions stack, built around the student journey.';

export const landingHeroContent: LandingHeroContent = {
  titleLines: [landingHeroTitleFirstLine, landingHeroTitleSecondLine],
  description: landingHeroDescription,
};

export interface VisionItemData {
  title: string;
  intro: string;
  description: string;
  titleAnimation: 'slideUp';
  titleAnimationBy: 'word';
  titleAnimationDuration?: number;
}

const defaultVisionTitleAnimation = {
  titleAnimation: 'slideUp',
  titleAnimationBy: 'word',
  titleAnimationDuration: 0.2,
} as const;

export const visionItems = [
  {
    title: 'Program-aware preparation',
    intro: 'Prepare with context, not guesswork.',
    description:
      'Cortexa helps students improve each part of the application with guidance shaped by the standards, culture, and expectations of specific programs.',
    ...defaultVisionTitleAnimation,
  },
  {
    title: 'One connected workflow',
    intro: 'From exam prep to essays, CVs, and deadlines, everything works together.',
    description:
      'Instead of juggling separate tools, students move through one structured journey built around what has to be done next.',
    ...defaultVisionTitleAnimation,
  },
  {
    title: 'Less friction, more momentum',
    intro: 'The Main Agent keeps the process moving.',
    description:
      'It tracks deadlines, organizes requirements, schedules preparation in calendars, and helps turn a stressful admissions cycle into a clear, manageable workflow.',
    ...defaultVisionTitleAnimation,
  },
] as const satisfies readonly VisionItemData[];

export interface LandingProductContent {
  variant: 'large' | 'medium';
  title: string;
  subtitle: string;
  description: string;
  badges: readonly string[];
  video?: LandingProductVideoContent;
  image?: string;
  enabled?: boolean;
  comingSoon?: boolean;
  ctaLabel: string;
  ctaTo: string;
}

export interface LandingProductVideoSourceSet {
  webm: string;
  mp4: string;
}

export interface LandingProductVideoContent {
  preview: LandingProductVideoSourceSet;
  dialog: LandingProductVideoSourceSet;
}

export const landingProductsSectionLabel = 'Products';

export const landingProductsSectionHeading =
  'Cortexa helps students prepare, write, organize, and submit stronger applications, with one intelligent system built for the full admissions journey.';

export const landingProductItems = [
  {
    variant: 'large',
    title: 'AI Test Prep',
    subtitle: 'Adaptive tutoring across every major exam.',
    description:
      'Personalized support for GMAT, GRE, IELTS, TOEFL, SAT, ACT, and more, with feedback that explains mistakes, adapts in real time, and keeps preparation structured and moving forward.',
    badges: ['Adaptive Learning'],
    video: {
      preview: {
        webm: '/media/landing/products/ai-test-prep/preview-lq.webm',
        mp4: '/media/landing/products/ai-test-prep/preview-lq.mp4',
      },
      dialog: {
        webm: '/media/landing/products/ai-test-prep/preview-hq.webm',
        mp4: '/media/landing/products/ai-test-prep/preview-hq.mp4',
      },
    },
    enabled: true,
    comingSoon: false,
    ctaLabel: 'Get started',
    ctaTo: ROUTES.LOGIN,
  },
  {
    variant: 'medium',
    title: 'Main Agent',
    subtitle: 'The operating system for your application journey.',
    description:
      'Coordinates deadlines, preparation plans, required documents, and submission steps across universities automatically so students always know what matters now and what comes next.',
    badges: ['Core product', 'Automated Orchestration'],
    image: '/media/landing/products/tba/tba-agent.jpg',
    enabled: true,
    comingSoon: true,
    ctaLabel: 'Get started',
    ctaTo: ROUTES.LOGIN,
  },
  {
    variant: 'medium',
    title: 'Exam Preparation',
    subtitle: 'Guidance that helps students express what makes them stand out.',
    description:
      'Structured support for essays and personal statements, shaped by the expectations of each target program. We do not write for students; we guide them to articulate their story, clarify their strengths, and present their potential in a way that feels authentic and gets noticed.',
    badges: ['Authentic Voice', 'Program-Aware Guidance'],
    image: '/media/landing/products/tba/tba-prep.jpg',
    enabled: true,
    comingSoon: true,
    ctaLabel: 'Get started',
    ctaTo: ROUTES.LOGIN,
  },
  {
    variant: 'medium',
    title: 'Resume Builder',
    subtitle: 'Turn experience into a CV with direction and impact.',
    description:
      'Transforms scattered achievements into a clear, compelling resume tailored to top schools and specific programs, with guidance that helps students identify, strengthen, and position their most relevant advantages.',
    badges: ['Strategic Positioning', 'CV Optimization'],
    image: '/media/landing/products/tba/tba-resume.jpg',
    enabled: true,
    comingSoon: true,
    ctaLabel: 'Get started',
    ctaTo: ROUTES.LOGIN,
  },
] as const satisfies readonly LandingProductContent[];
